import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product SKU with Natterjacks-specific formatting.
 * This component listens for variant update events and updates the SKU display accordingly.
 * 
 * Natterjacks' customisations:
 * - SKUs in Shopify are formatted as [style code]-[item code]
 * - They are prefixed with zeroes to pad them to a specified number of digits
 * - We transform these raw values to extract the [item code] only
 * - Remove leading zeroes from the item code
 * - Apply optional text prefix (such as "Code: ")
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class NatterjacksProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  /**
   * Transforms a raw SKU into the Natterjacks display format.
   * Extracts the item code (second part after hyphen) and removes leading zeroes.
   * @param {string} rawSku - The raw SKU from Shopify (e.g., "001-00123")
   * @returns {string} The formatted item code (e.g., "123")
   */
  transformSku(rawSku) {
    if (!rawSku) return '';
    
    const skuParts = rawSku.split('-');
    if (skuParts.length < 2) return rawSku; // Fallback if SKU doesn't match expected format
    
    const itemCode = skuParts[1];
    // Convert to number to remove leading zeroes, then back to string
    const itemCodeNumber = parseInt(itemCode, 10);
    return isNaN(itemCodeNumber) ? itemCode : itemCodeNumber.toString();
  }

  /**
   * Updates the SKU.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Use the variant data from the event
    // The variant is in event.detail.resource
    if (event.detail.resource) {
      const variantSku = event.detail.resource.sku || '';

      if (variantSku) {
        // Transform the SKU using Natterjacks logic
        const transformedSku = this.transformSku(variantSku);
        
        // Get the prefix from data attribute if set
        const prefix = this.dataset.prefix || '';
        const displaySku = prefix ? `${prefix} ${transformedSku}` : transformedSku;
        
        // Show the component and update the SKU
        this.style.display = 'block';
        this.refs.sku.textContent = displaySku;
      } else {
        // Hide the entire component when SKU is empty
        this.style.display = 'none';
        this.refs.sku.textContent = '';
      }
    }
  };
}

if (!customElements.get('nj-product-sku')) {
  customElements.define('nj-product-sku', NatterjacksProductSkuComponent);
}
